import React, { useEffect, useId, useMemo, useRef, useState } from "react";

import CartaEmDestaque from "./CartaEmDestaque";
import {
  colecaoPorRegiao,
  dataDaConquista,
  percentualDaColecao,
  regiaoAbertaPorPadrao,
  resumoDaColecao
} from "./colecao";
import { variaveisDaRegiao } from "./estiloDaRegiao";
import { useInclinacao3d } from "./inclinacao3d";
import { useRecolhimentoAnimado } from "./recolhimentoAnimado";

import "../styles/colecaoDeCartas.css";

/* O QUE CONTA COMO CARTA para o efeito 3D. `button` no seletor de propósito: a
   silhueta usa a mesma classe (é o que faz o buraco ocupar exatamente o lugar da carta
   que falta), mas é um `<span>` — e carta que reage ao cursor promete um toque que ali
   não existe. */
const SELETOR_DA_CARTA = "button.colecao__carta-botao";

/**
 * A COLEÇÃO DE CARTAS do aluno — o álbum de figurinhas da trilha lúdica, com os
 * buracos à vista.
 *
 * Componente de APRESENTAÇÃO: recebe o acervo (do catálogo) e o mapa do que o aluno
 * tem, e não sabe de onde nenhum dos dois veio. Quem faz HTTP é o host — a regra do
 * módulo é que nenhum import saia dele (ver ../README.md).
 *
 * POR QUE NÃO É A TELA DE ENCERRAMENTO OUTRA VEZ
 * ----------------------------------------------
 * São duas telas de carta e elas mostram coisas diferentes de propósito (D12 do
 * Plano_Trilha_Persistencia_e_Regioes.md):
 *
 *   encerramento  só o que o aluno ganhou NAQUELA trilha, para celebrar o fim dela;
 *   coleção       o acervo COMPLETO, atravessando regiões e trilhas, com silhueta no
 *                 lugar do que falta.
 *
 * Por isso aqui não há mascote falando, não há confete e não há digitação: o aluno
 * chegou por vontade própria, pelo menu, e o que ele quer é conferir o álbum. A
 * celebração é da trilha; esta tela é do acervo.
 *
 * AS REGIÕES FICAM EMPILHADAS, e não em abas. Cada uma é um painel com o degradê do
 * bioma dela — que é o que distingue Caatinga de Amazônia antes de o aluno ler o
 * nome. Abas economizariam rolagem e cobrariam duas coisas: um clique para descobrir
 * que existe outra região (numa tela cujo trabalho é justamente mostrar que existe
 * mais para colecionar) e a gestão de foco por setas que o padrão ARIA exige, que
 * mal feita é pior que não ter. A rolagem vertical é o que se espera de um álbum.
 *
 * E CADA PAINEL RECOLHE, o que é o contrário de abas e não uma abas disfarçada: o que
 * o botão esconde é só a GRADE. O cabeçalho da região — mascote, bioma, contador e
 * barra de progresso — fica na tela recolhido ou não, então continua sendo a região
 * fechada que diz "existe outro bioma, e você tem 3 de 22 dele". O que sai é a
 * rolagem: são 22 cartas por região, 110 nas cinco, e o aluno que quer conferir uma
 * atravessava o álbum inteiro para chegar na quinta.
 *
 * @param {Array}    acervo      de `conteudo/acervoDeCartas()`
 * @param {object}   possuidos   mapa cardId → data de conquista
 * @param {boolean}  carregando  a coleção ainda está vindo do servidor
 * @param {boolean}  [cabecalho] desenhar o próprio título e contador. Padrão `true`,
 *                              que é o que serve a quem plugar o módulo noutro host.
 *                              A página de conquistas do AVA passa `false`: lá a
 *                              moldura da seção já dá o título e o contador a TODOS os
 *                              tipos de conquista, e dois cabeçalhos empilhados eram
 *                              exatamente o dialeto que aquela moldura veio desfazer.
 * @param {Function} [onResumo]  avisa quem monta: `{ conquistadas, total }`
 */
function ColecaoDeCartas({
  acervo = [],
  possuidos = {},
  carregando = false,
  cabecalho = true,
  onResumo
}) {
  /* O cruzamento é O(acervo) e roda a cada renderização do pai — a página de
     conquistas re-renderiza quando a lista de medalhas chega. `useMemo` porque as
     cartas viram objetos novos a cada chamada, e objeto novo é re-render de cada
     carta da grade: 44 hoje, 110 nas cinco regiões. */
  const regioes = useMemo(
    () => colecaoPorRegiao(acervo, possuidos),
    [acervo, possuidos]
  );
  const resumo = useMemo(() => resumoDaColecao(regioes), [regioes]);

  /* O RESUMO SOBE PARA QUEM MONTA, quando pedido — é o que permite ao cabeçalho da
     seção mostrar "12 de 44" sem a coleção desenhar um cabeçalho próprio.
     Num efeito e não no corpo da renderização: chamar o callback do pai enquanto se
     renderiza é `setState` durante render, que o React 18 reclama e que pode entrar em
     laço. E só quando a coleção JÁ CHEGOU: durante o carregamento os números são "0 de
     44", e publicá-los faria o contador anunciar um álbum vazio a quem tem cartas. */
  useEffect(() => {
    if (!onResumo || carregando) return;
    onResumo(resumo);
  }, [onResumo, carregando, resumo]);

  /* O QUE O ALUNO ABRIU E FECHOU, e só isso: as regiões que ele não tocou não estão
     aqui e caem no padrão (`regiaoAbertaPorPadrao`), que é uma aberta. Guardar as
     abertas por inteiro obrigaria a semear o estado quando as regiões chegam do
     servidor, num efeito — e o efeito roda DEPOIS da primeira pintura, então o álbum
     apareceria com tudo aberto e recolheria sozinho na frente do aluno.

     A escolha dele vence o padrão para sempre nesta visita: quem abriu a Amazônia não
     a vê fechar porque uma carta nova chegou e mudou qual região seria a padrão. E não
     é acordeão exclusivo — abrir uma não fecha as outras: comparar duas regiões é
     coisa de álbum, e fechar o que o aluno acabou de abrir é a interface desfazendo o
     gesto dele. */
  const idAbertaPorPadrao = useMemo(
    () => regiaoAbertaPorPadrao(regioes),
    [regioes]
  );
  const [alternadas, setAlternadas] = useState({});

  /* O PREFIXO DOS IDS vem do React e não do id da região, porque `aria-controls` só
     funciona com id único no DOCUMENTO — e nada impede duas coleções na mesma página
     (o módulo é plugável; a página de conquistas monta uma, hoje). `useId` é a única
     forma de ter isso sem contador global. */
  const prefixo = useId();

  /* A alternância é FUNCIONAL (`anterior =>`) e não lê `alternadas` de fora: dois
     toques rápidos no mesmo botão são duas atualizações no mesmo lote, e a segunda
     leria o mapa velho — o painel piscaria e voltaria para onde estava. */
  function alternarRegiao(id) {
    setAlternadas(anterior => ({
      ...anterior,
      [id]: !regiaoEstaAberta(anterior, id, idAbertaPorPadrao)
    }));
  }

  /* A carta aberta guarda o ID, não o objeto — mesma razão do encerramento: se a
     lista mudar enquanto o destaque está aberto, o painel acompanha a carta certa
     em vez de segurar uma cópia velha. */
  const [idAberto, setIdAberto] = useState(null);
  const cartaAberta = useMemo(() => {
    if (!idAberto) return null;
    for (let i = 0; i < regioes.length; i += 1) {
      const achada = regioes[i].cartas.find(carta => carta.id === idAberto);
      if (achada) return achada;
    }
    return null;
  }, [idAberto, regioes]);

  /* A PÁGINA NÃO ROLA POR TRÁS DO DESTAQUE. A seção da coleção é comprida (duas
     regiões já passam de duas telas), e sem isto a roda do mouse sobre o véu escuro
     rolaria a página inteira embaixo dele — o aluno fecha a carta e não está mais
     onde estava. É seguro aqui porque a página de conquistas não tem modal do AVA
     concorrendo pelo `overflow` do body; o valor anterior é restaurado, não
     assumido. */
  useEffect(() => {
    if (!cartaAberta) return undefined;

    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = anterior;
    };
  }, [cartaAberta]);

  /* A CARTA SOB O CURSOR INCLINA EM 3D, o mesmo efeito do álbum do encerramento e o
     mesmo hook — é a mesma arte e o mesmo gesto (olhar a carta de perto), então
     inclinar num lugar e não no outro seria a mesma tela respondendo de dois jeitos.

     UM LISTENER NA SEÇÃO INTEIRA, e não um por região: a delegação do hook já resolve
     qualquer número de regiões, e o contrário seria N listeners para no máximo uma
     carta se mover.

     Desligado enquanto o destaque está aberto: atrás do véu escuro nada do que se
     move é visível, e o `pointermove` continuaria rodando a cada pixel. */
  const grades = useRef(null);
  useInclinacao3d(grades, SELETOR_DA_CARTA, !cartaAberta);

  return (
    /* `.trilha-ludica` é o PORTADOR DOS TOKENS `--tl-*` (é onde o CSS do módulo os
       declara), e `.colecao` desliga o degradê e o padding que ela traz para o
       tabuleiro: aqui o fundo é de cada painel de região, não da seção. Duas classes
       vencem uma na especificidade, então não precisa de `!important`. */
    <section className="trilha-ludica colecao" ref={grades}>
      <header className="colecao__cabecalho">
        {/* O TÍTULO E A BARRA GLOBAL SÃO DE QUEM SE BASTA. Na página de conquistas
            quem os dá é a moldura da seção, para TODOS os tipos de conquista — e dois
            títulos empilhados eram o dialeto que aquela moldura veio desfazer. Fora do
            AVA (o módulo é plugável) não há moldura nenhuma, e aí eles voltam. */}
        {cabecalho && <h5 className="colecao__titulo">Coleção de cartas</h5>}

        {/* A LINHA DE ESTADO FICA SEMPRE, com ou sem cabeçalho, porque nenhuma das
            três coisas que ela diz está em outro lugar: que a coleção está vindo, onde
            as cartas aparecem para quem não tem nenhuma, e que a carta abre ao toque.
            O que sai com o cabeçalho é só o "12 de 44" — esse a moldura repete. */}
        <p className="colecao__resumo">
          {carregando && "Carregando sua coleção…"}

          {!carregando && resumo.conquistadas === 0 && (
            <>
              {cabecalho && (
                <>
                  Você ainda não descobriu nenhuma das{" "}
                  <strong>{resumo.total}</strong> cartas.{" "}
                </>
              )}
              Suas cartas aparecem aqui quando você percorre as trilhas das suas
              atividades.
            </>
          )}

          {!carregando && resumo.conquistadas > 0 && (
            <>
              {cabecalho && (
                <>
                  Você já descobriu{" "}
                  <strong>
                    {resumo.conquistadas} de {resumo.total}
                  </strong>{" "}
                  cartas.{" "}
                </>
              )}
              {/* O CONVITE AO GESTO fica AQUI, uma vez, e não em cada carta. A placa
                  da carta diz o NOME, que não anuncia que há mais para ver; o que
                  anuncia é o cursor de lupa e o crescimento sob o ponteiro, e nenhum
                  dos dois existe no celular. Uma linha resolve a descoberta sem pôr um
                  "ver mais" em cada uma das 22. */}
              Toque numa carta para ver de perto.
            </>
          )}
        </p>

        {cabecalho && !carregando && (
          <BarraDeProgresso
            conquistadas={resumo.conquistadas}
            total={resumo.total}
          />
        )}
      </header>

      {/* NADA DE CARTAS ENQUANTO A COLEÇÃO NÃO CHEGA, e o motivo é o que se veria no
          lugar: sem a resposta do servidor toda carta é silhueta, então o aluno com
          15 cartas abriria a página num álbum inteiramente vazio e veria 15 delas
          "acenderem" um instante depois. Um álbum vazio é justamente a mensagem que
          esta tela dá a quem não tem nada — mostrá-la a quem tem é dizer a coisa
          errada, ainda que por meio segundo.

          O esqueleto guarda a altura de um painel para a página não pular quando as
          regiões entram. */}
      {carregando && <div className="colecao__esqueleto" aria-hidden="true" />}

      {!carregando &&
        regioes.map(regiao => {
          const aberta = regiaoEstaAberta(
            alternadas,
            regiao.id,
            idAbertaPorPadrao
          );
          const idDaGrade = `${prefixo}-${regiao.id}`;

          return (
            <article
              key={regiao.id}
              className="colecao__regiao"
              /* `data-tl-region` pelo mesmo motivo do tabuleiro: é o gancho pelo qual
                 uma medição sabe QUAL região está na tela. E o `style` é o que pinta o
                 bioma — as custom properties declaradas aqui valem para este painel e
                 seus descendentes, então duas regiões empilhadas têm cada uma a sua
                 paleta sem um bloco CSS por bioma (ver `estiloDaRegiao.js`). */
              data-tl-region={regiao.id}
              style={variaveisDaRegiao(regiao)}
            >
              <header className="colecao__regiao-cabecalho">
                {/* O BOTÃO É O CABEÇALHO INTEIRO, dentro do heading — é o padrão de
                    disclosure do ARIA (heading > button) e é o que dá um alvo do
                    tamanho do painel a quem toca com o dedo. Por isso tudo aqui dentro
                    é `<span>` e `<img>`: um `<h6>` ou um `<p>` dentro de `<button>` é
                    HTML inválido, e leitor de tela que se recusa a entrar num botão mal
                    formado anuncia "botão" e mais nada.

                    O `aria-expanded` é o que diz o estado — nada de trocar o texto do
                    botão entre "Mostrar" e "Esconder", que é a versão que só funciona
                    para quem vê. */}
                <h6 className="colecao__regiao-titulo">
                  <button
                    type="button"
                    className="colecao__regiao-alternar"
                    aria-expanded={aberta}
                    aria-controls={idDaGrade}
                    onClick={() => alternarRegiao(regiao.id)}
                  >
                    {regiao.mascote && regiao.mascote.cabeca && (
                      /* A CABEÇA do mascote, no medalhão — o mesmo asset e o mesmo
                         enquadramento do nó de fase rara. `alt=""` porque o nome do
                         bioma está escrito ao lado: para o leitor de tela a figura é
                         decoração, e descrevê-la seria repetir em voz alta o que já vem
                         no texto. */
                      <img
                        className="colecao__regiao-mascote"
                        src={regiao.mascote.cabeca}
                        alt=""
                        loading="lazy"
                        decoding="async"
                      />
                    )}

                    <span className="colecao__regiao-nome">
                      {/* O BIOMA É O TÍTULO e a região é o subtítulo, e não o
                          contrário. O aluno reconhece "Amazônia"; "Norte" é a chave que
                          o professor escolhe e o banco guarda. São a MESMA coisa com
                          dois nomes (ver `conteudo/index.js`), e aqui vale o nome que a
                          criança lê. */}
                      <span className="colecao__regiao-bioma">
                        {regiao.bioma}
                      </span>
                      <span className="colecao__regiao-regiao">
                        Região {regiao.nome}
                      </span>
                    </span>

                    <span className="colecao__regiao-contador">
                      <strong>{regiao.conquistadas}</strong>
                      <span aria-hidden="true"> / </span>
                      <span className="colecao__regiao-contador-total">
                        <span className="trilha-ludica__so-leitor">de </span>
                        {regiao.total}
                      </span>
                    </span>

                    {/* A SETA é o que anuncia que o painel abre, e é desenhada em SVG
                        porque o módulo não tem fonte de ícone e um caractere "▾" muda de
                        forma e de linha de base em cada sistema. `aria-hidden` porque o
                        estado já está no `aria-expanded` — o leitor de tela o anuncia, e
                        uma seta descrita seria a mesma informação duas vezes. */}
                    <svg
                      className="colecao__regiao-seta"
                      viewBox="0 0 24 24"
                      width="24"
                      height="24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        d="M6 9.5 12 15.5 18 9.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </h6>
              </header>

              {/* A BARRA FICA FORA DO RECOLHIMENTO, de propósito: recolhida, a região
                  tem de continuar dizendo quanto dela o aluno tem — é essa a informação
                  que faz ele querer abrir. */}
              <BarraDeProgresso
                conquistadas={regiao.conquistadas}
                total={regiao.total}
                rotulo={`Cartas da ${regiao.bioma}`}
              />

              {/* AS CARTAS FICAM NO DOM quando a região está recolhida, e `hidden` é o
                  que as esconde. Não é descuido de performance: `loading="lazy"` faz o
                  navegador não baixar imagem nenhuma de dentro de um `display: none`, e
                  o que sobra é o nó do HTML. Em troca, o Ctrl+F do navegador continua
                  achando o nome de uma carta guardada num painel fechado, e o
                  `aria-controls` do botão aponta para um elemento que existe. */}
              <GradeDaRegiao id={idDaGrade} aberta={aberta}>
                {regiao.cartas.map(carta => (
                  <li
                    key={carta.id}
                    /* `--raro` fica na CÉLULA e não na carta porque o brilho holográfico
                       precisa de `overflow: hidden` no botão, e ligá-lo em toda carta
                       cortaria o `drop-shadow` das outras — o seletor desce do item.

                       E só quando a carta foi conquistada: numa silhueta, o brilho
                       anunciaria "aqui tem uma rara" antes de o aluno ter feito nada por
                       ela. Não há classe para o buraco: quem o distingue é o
                       `--silhueta` no próprio elemento da carta, e uma segunda classe
                       sem regra nenhuma é código morto que a próxima pessoa vai procurar
                       no CSS. */
                    className={`colecao__item${
                      carta.tipo === "raro" && carta.possuida
                        ? " colecao__item--raro"
                        : ""
                    }`}
                  >
                    {carta.possuida ? (
                      <CartaConquistada
                        carta={carta}
                        onAbrir={() => setIdAberto(carta.id)}
                      />
                    ) : (
                      <Silhueta carta={carta} />
                    )}
                  </li>
                ))}
              </GradeDaRegiao>
            </article>
          );
        })}

      {cartaAberta && (
        /* A LUPA existe só para dar à carta aberta um contexto FIXO. `CartaEmDestaque`
           é `absolute` (no encerramento ele cobre a área da trilha, de propósito), e
           aqui a seção da coleção é comprida: uma carta aberta no fim dela
           centralizaria fora da vista. `fixed` no pai resolve sem tocar no
           componente, que continua servindo às duas telas. */
        <div className="colecao__lupa">
          <CartaEmDestaque
            card={cartaAberta}
            detalhe={detalheDaConquista(cartaAberta)}
            onFechar={() => setIdAberto(null)}
          />
        </div>
      )}
    </section>
  );
}

/**
 * A GRADE DE UMA REGIÃO, que abre e fecha animada.
 *
 * Existe como componente por uma razão só, e é do React: `useRecolhimentoAnimado`
 * precisa de um ref e de um estado POR REGIÃO, e hook não se chama dentro de um
 * `.map`. Envolver o `<ul>` num componente é o menor recorte que resolve isso — a
 * marcação e as classes continuam as mesmas.
 *
 * `hidden` vem do hook e não de `aberta`: ao fechar, o atributo só entra quando a
 * altura já chegou a zero, senão `display: none` mataria a animação antes de ela
 * começar. Ver o cabeçalho de `recolhimentoAnimado.js`.
 */
function GradeDaRegiao({ id, aberta, children }) {
  const grade = useRef(null);
  const { oculto } = useRecolhimentoAnimado(grade, aberta);

  return (
    <ul ref={grade} id={id} className="colecao__grade" hidden={oculto}>
      {children}
    </ul>
  );
}

/* Se o painel daquela região está aberto: o que o aluno alternou, quando alternou, e
   o padrão do módulo enquanto ele não tocou em nada. `hasOwnProperty` porque `false`
   ("eu FECHEI esta") é resposta, e com verdade/falsidade ela viraria "não sei" — a
   região que o aluno fechou reabriria na próxima renderização do pai. */
function regiaoEstaAberta(alternadas, id, idAbertaPorPadrao) {
  return Object.prototype.hasOwnProperty.call(alternadas, id)
    ? alternadas[id]
    : id === idAbertaPorPadrao;
}

/* A data em que a carta entrou na coleção, quando o servidor souber dizer.
   Nem toda carta tem: `obtained_at` é preenchido na reconciliação, e um registro
   gravado antes disso pode vir sem. Sem data, a linha simplesmente não aparece —
   nada de "conquistada em —". */
function detalheDaConquista(carta) {
  const data = dataDaConquista(carta.obtidaEm);
  return data ? `Você descobriu esta carta em ${data}` : null;
}

/**
 * A carta que o aluno TEM: a arte, e o nome dela numa placa embaixo.
 *
 * A ARTE NÃO TEM MOLDURA BRANCA EM VOLTA, e isso é regra da arte e não gosto: os
 * assets são RGBA com a moldura entalhada DESENHADA até a borda, então um fundo branco
 * com padding em volta desenha uma segunda moldura por cima da que o ilustrador já
 * fez — duas bordas concêntricas, e a de fora ganhando da de dentro. O encerramento já
 * trata as cartas assim (a arte solta, com sombra própria), e é a mesma arte.
 *
 * A PLACA BRANCA É SÓ DO NOME. A DESCRIÇÃO NÃO ENTRA, e o corte é exatamente onde a
 * redundância começava: as duas juntas eram um bloco de texto por carta, e a pílula
 * mais longa do acervo tem 190 caracteres — vinte e duas delas viram uma parede que
 * afoga as figuras. O nome sozinho é uma etiqueta; ele nomeia o que a criança está
 * vendo, e é o que se lê de relance ao varrer a grade procurando uma carta.
 *
 * A curiosidade fica no clique, onde cabe inteira em vez de cortada em três linhas.
 *
 * A placa também é o que dá FUNDO ao texto: as fileiras de baixo da grade caem na parte
 * escura do degradê do bioma, onde letra solta desaparece — e em que faixa cada fileira
 * cai depende de quantas cartas a região tem. É o mesmo raciocínio da placa do nome no
 * álbum do encerramento.
 *
 * O BOTÃO É SÓ A ARTE, e a placa fica fora dele. Assim o efeito 3D inclina a figura e
 * não o texto (texto girando em perspectiva fica ilegível), e o alvo de toque continua
 * sendo a carta, que é onde a criança mira.
 */
function CartaConquistada({ carta, onAbrir }) {
  return (
    <>
      <button
        type="button"
        className="colecao__carta-botao"
        onClick={onAbrir}
        /* O rótulo diz o nome E a ação, porque "Queimadas" sozinho não avisa que há
           algo para ler — mesma razão do rótulo no encerramento. O nome se repete
           entre o rótulo e a placa, e é de propósito: o rótulo SUBSTITUI o conteúdo
           do botão para o leitor de tela (a arte é `alt=""`), e a placa está fora
           dele. Sem o rótulo, a grade seria 22 botões chamados "botão". */
        aria-label={`${carta.nome} — ver a carta de perto`}
      >
        <img
          className="colecao__carta-arte"
          src={carta.asset}
          alt=""
          width="420"
          height="588"
          /* `lazy`/`async` porque aqui MUITAS cartas aparecem juntas — 44 hoje, 110
             nas cinco regiões, a 59 KB cada. É a única tela do módulo em que isso
             muda algo de verdade, e está no plano por isso (§9 do
             Plano_Trilha_Persistencia_e_Regioes.md). `width`/`height` são os do
             arquivo: servem para o navegador reservar a caixa na proporção certa
             antes de a imagem chegar, o que é o que impede a grade de pular de
             altura enquanto elas carregam. */
          loading="lazy"
          decoding="async"
          draggable={false}
        />
        {carta.tipo === "raro" && (
          <span className="colecao__selo-raro">Rara</span>
        )}
      </button>

      <span className="colecao__placa">
        <span className="colecao__carta-nome">{carta.nome}</span>
      </span>
    </>
  );
}

/**
 * O LUGAR VAZIO de uma carta que falta.
 *
 * A arte aparece EMBAÇADA e sem cor, e é aí que está o trabalho desta tela: um
 * quadrado cinza diria "faltam 10" e a silhueta diz "falta ESTA" — dá o que
 * colecionar em vez de dar um número. É o que o álbum de figurinhas de papel faz com
 * a sombra impressa no lugar.
 *
 * O NOME E A DESCRIÇÃO NÃO APARECEM, e isso é decisão e não esquecimento: a pílula é
 * o conteúdo educativo da carta, e é o que o mascote revela na cerimônia quando o
 * aluno conquista. Lê-la aqui de graça gastaria a revelação antes de ela acontecer —
 * a curiosidade sobre o que está embaçado é justamente o que faz o aluno querer
 * percorrer a próxima trilha.
 *
 * NÃO É BOTÃO nem é focável: não há nada para abrir. Um botão que abre um painel
 * dizendo "ainda não" é um beco para quem navega por teclado, e seriam dez becos
 * seguidos.
 */
function Silhueta({ carta }) {
  return (
    <>
      {/* `--silhueta` no mesmo elemento que a carta conquistada usa, e não um bloco
          próprio: o buraco tem de ocupar EXATAMENTE o lugar da carta que falta, senão a
          grade se rearranja conforme o aluno conquista e ele perde a referência de onde
          as coisas estavam. O que muda é o que a classe desliga — o 3D, o brilho, o
          cursor e a sombra.

          `<span>` e não `<button>`: sem o botão, o efeito 3D também não a alcança (o
          hook procura `button.colecao__carta-botao`), o que é o certo — carta que reage
          ao cursor promete um toque que não existe. */}
      <span className="colecao__carta-botao colecao__carta-botao--silhueta">
        {/* `aria-hidden` na figura e na interrogação: as duas dizem a mesma coisa que a
            placa abaixo, e sem isto o leitor de tela repetiria o item três vezes. */}
        <img
          className="colecao__carta-arte"
          src={carta.asset}
          alt=""
          width="420"
          height="588"
          loading="lazy"
          decoding="async"
          draggable={false}
          aria-hidden="true"
        />
        <span className="colecao__interrogacao" aria-hidden="true">
          ?
        </span>
      </span>

      {/* A PLACA DA SILHUETA existe pela mesma razão que a da carta conquistada, mais
          uma: ela mantém a célula da MESMA ALTURA das vizinhas. Sem ela, uma fileira
          com cartas e buracos misturados ficaria serrilhada, e a serrilha é o que faz
          uma grade parecer defeito em vez de álbum.

          O que ela NÃO diz é o nome da carta: a pílula e o título são o que o mascote
          revela na cerimônia, e lê-los aqui de graça gastaria a revelação antes de ela
          acontecer. É a curiosidade sobre o que está embaçado que faz o aluno querer a
          próxima trilha. */}
      <span className="colecao__placa colecao__placa--vazia">
        <span className="colecao__carta-nome colecao__carta-nome--oculto">
          Carta a descobrir
        </span>
      </span>
    </>
  );
}

/**
 * A barra de progresso, com o número dito em texto para quem não vê a barra.
 *
 * `role="img"` com `aria-label` em vez de `role="progressbar"`: progressbar é para
 * operação em andamento (um upload), e o leitor de tela a anuncia como algo que vai
 * mudar sozinho. Isto é um FATO — quanto do álbum está cheio.
 */
function BarraDeProgresso({ conquistadas, total, rotulo }) {
  const percentual = percentualDaColecao(conquistadas, total);
  const texto = `${conquistadas} de ${total} cartas descobertas`;

  return (
    <div
      className="colecao__barra"
      role="img"
      aria-label={rotulo ? `${rotulo}: ${texto}` : texto}
    >
      <span
        className="colecao__barra-cheia"
        style={{ width: `${percentual}%` }}
      />
    </div>
  );
}

export default ColecaoDeCartas;
